// src/com/bbs_pkg/user_bbs/UserBbsFiles.js
import { useMemo, useRef, useState } from 'react';
import { styled  } from '@mui/material/styles';
import { Box } from '@mui/system';
import { Button, IconButton } from '@mui/material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { saveAs } from 'file-saver';
import { useSnackbar } from 'notistack';
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import { userInfoRepo } from '../../../value/AppModel';
import ValueMisc from '../../../value/ValueMisc';
import { useAlertSetter } from '../../../api/app-hook';
import { NonCapButton } from '../../../common/VzmStyled';
import { useConfirmDialog } from '../../../common/AppConfirmDialog';
import { AppObject, AppStyle, AppWord } from '../../../value/AppConst';
import {
    ADD_USER_BBS_INFO_FILE, GET_USER_BBS_INFO_DOWNLOAD_URL_PRE_SIGNED,
    GET_USER_BBS_INFO_UPLOAD_URL_PRE_SIGNED, REMOVE_USER_BBS_INFO_FILE
} from './UserBbsInfoGql';

const MARGIN_BACK = 18;

const ListContainer = styled(Box)({
    marginTop: MARGIN_BACK, border: '1px solid #ddd', borderRadius: 5, padding: 10,
});

// ListContainer의 상단의 border에 걸쳐 표시할 제목
const FileLinkTitle = styled(Box)({
    fontSize:'0.8rem', color: AppStyle.PrimaryRGB, marginTop: -MARGIN_BACK, marginLeft: 10,
    padding: '0px 10px 0px 10px',
});

const FileLinkRow = styled(Box)({
    display: 'flex', alignItems: 'center', padding: 0, borderRadius: 5,
    '&:hover': { backgroundColor: '#e0e0e0' },
});
const FileLinkBox = styled(Box)({ flexGrow:1, });

/**
 * 게시판의 글에 첨부된 파일 목록.
 * 게시자는 파일을 업로드, 삭제할 수 있음.
 * 다운로드는 presigned URL을 이용함.
 * 업로드 역시 presigned URL을 이용함.
 * @param {*} param0 
 * @returns 
 */
export default function UserBbsFiles({
    item,
    onRequestReload, // reload the UserBbsInfo item after file upload/delete
    actionDisabled=false,
}) {
    const sessionInfo = useReactiveVar(userInfoRepo);
    const [uploading, setUploading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const openConfirmDialog = useConfirmDialog();
    const addAlert = useAlertSetter();
    const files = useMemo(() => {
        const fileList = [];
        for(let i=1; i<=5; i++) {
            const fileKey = `fileName${i}`;
            if(item[fileKey]) {
                fileList.push({index: i, name: item[fileKey]});
            }
        }
        return fileList;
    }, [item]);
    const isPoster = useMemo(() => {
        return sessionInfo.user.userId <= item.regUserId;
    }, [sessionInfo, item]);
    const fileInputRef = useRef(null);
    const downloadFileRef = useRef(null);
    const [getUploadUrl] = useLazyQuery(GET_USER_BBS_INFO_UPLOAD_URL_PRE_SIGNED, 
        {...AppObject.NoCachedFetch});
    const [getDownloadUrl, responseGetDownloadUrl] = useLazyQuery(
        GET_USER_BBS_INFO_DOWNLOAD_URL_PRE_SIGNED, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => onCompleteGetPresignedUrl(data, option),
        onError: (error) => addAlert({title: "파일다운로드", message: [error.message]})
        }
    );

    const [addUserBbsInfoFile, responseAdd] = useMutation(ADD_USER_BBS_INFO_FILE, {
        onCompleted: (data) => onCompleteAdd(data),
        onError: (error) => addAlert({title: "파일첨부", message: [error.message]})
    });
    const [removeUserBbsInfoFile, responseRemove] = useMutation(REMOVE_USER_BBS_INFO_FILE, {
        onCompleted: (data) => onCompleteRemove(data),
        onError: (error) => addAlert({title: "파일삭제", message: [error.message]})
    });
    
    const onCompleteAdd = (data) => {
        if(data.userBbsInfoAddFile.ok) {
            enqueueSnackbar('파일이 성공적으로 업로드되었습니다.', { variant: AppStyle.mui.success, autoHideDuration: 2000 });
            onRequestReload(item.bbsInfoSeq);
        }
        else //setResponseAlert({open:true, resultData: data.bbsInfoAdd, title: ErrorTitle.Add});
            addAlert({title: "파일첨부", message: [data.userBbsInfoAddFile.message]});
    };

    const onCompleteRemove = (data) => {
        if(data.userBbsInfoRemoveFile.ok) {
            enqueueSnackbar('파일이 성공적으로 삭제되었습니다.', { variant: AppStyle.mui.success, autoHideDuration: 2000 });
            onRequestReload(item.bbsInfoSeq);
        }
        else addAlert({title: "파일삭제", message: [data.userBbsInfoRemoveFile.message]});
    };

    const downloadFile = async (url, filename) => {
        try {
            // 1. URL을 통해 데이터를 Blob으로 가져옴
            setDownloading(true);
            const response = await fetch(url);
            const blob = await response.blob();
            
            // 2. file-saver를 통해 저장 (브라우저 뷰어 실행 없이 즉시 다운로드)
            saveAs(blob, filename);
        } catch (error) {
            addAlert({title: "파일다운로드", message: ['파일 다운로드 중 오류가 발생했습니다.', error.message || error.toString()]});
        } finally {
            setDownloading(false);
        }
    };

    const onCompleteGetPresignedUrl = (data, option) => {
        if(data && data.userBbsInfoGetDownloadUrlPreSigned) {
            const url = data.userBbsInfoGetDownloadUrlPreSigned.preSignedUrl;
            if(url) {
                downloadFile(url, downloadFileRef.current);
            }
            else {
                addAlert({title: "파일다운로드", message: ["파일 다운로드 URL을 가져오지 못했습니다."]});
            }
        }
        else {
            addAlert({title: "파일다운로드", message: ["파일 다운로드 정보를 가져오지 못했습니다."]});
        }
    };

    const onClickDownload = (fileIndex, fileName) => {
        downloadFileRef.current = fileName;
        getDownloadUrl({
            variables: {
                bbsInfoSeq: item.bbsInfoSeq,
                fileId: fileIndex,
            }
        });
    };

    const removeFile = (fileIndex) => {
        // Call REMOVE_USER_BBS_INFO_FILE and then onRequestReload()
        removeUserBbsInfoFile({
            variables: {
                userBbsInfo: {
                    bbsInfoSeq: item.bbsInfoSeq,
                    fileId: fileIndex,
                }
            }
        });
    };

    const onClickRemove = async (fileIndex) => {
        const confirm = await openConfirmDialog({
            title: '파일 삭제',
            content:['선택한 파일을 삭제하시겠습니까?', '삭제한 파일은 복구할 수 없습니다.'],
        });

        if(confirm) removeFile(fileIndex);
    };

    const reflectToDatabaseOnAdd = (fileName) => {
        let idx = 0;
        for(let i=1; i<=5; i++) {
            const fkey = 'fileName'+i;
            if(!item[fkey]) {
                idx = i;
                break;
            }
        }
        
        if(idx > 0) {
            addUserBbsInfoFile({
                variables: {
                    userBbsInfo: {
                        bbsInfoSeq: item.bbsInfoSeq,
                        fileId: idx,
                        fileName1: fileName,
                    }
                }
            });
        }
        else {
            addAlert({title: "파일첨부", message: ["더 이상 파일을 첨부할 수 없습니다. (최대 5개)"]});
        }
    };

    const onChooseFile = async (event) => {
        if(event.target.files.length > 0) {
            const file = event.target.files[0];
    
            if (file) {
                const sanitizedName = ValueMisc.sanitizeFilename(file.name);
                // 먼저 기존에 같은 이름의 파일이 있는지부터 검사.
                const found = files.find(f => f.name === sanitizedName);
                if(found) {
                    addAlert({title: "파일첨부", message: ["같은 이름의 파일이 이미 존재합니다. 다른 이름의 파일을 선택해 주세요."]});
                    return;
                }

                try {
                    setUploading(true);
                    // const contentType = file.type || AppWord.OCTET_STREAM_TYPE;
                    // SVG의 경우 업로드할 때 403 오류가 발생. 그래서 강제로 항상 octet-stream으로 지정
                    const contentType = AppWord.OCTET_STREAM_TYPE;

                    // 1. Backend로부터 Pre-signed URL 및 ObjectKey 수신
                    const { data } = await getUploadUrl({
                        variables: {
                            bbsInfoSeq: item.bbsInfoSeq,
                            filename: sanitizedName,
                            contentType
                        }
                    });

                    const { preSignedUrl } = data.userBbsInfoGetUploadUrlPreSigned;

                    if(preSignedUrl) {
                        // 2. S3로 파일 업로드
                        const response = await fetch(preSignedUrl, {
                            method: AppWord.PUT,
                            headers: {
                                'Content-Type': contentType,
                            },
                            body: file, // // 바이너리 파일 객체 직접 전달
                        });

                        // 3. fetch는 네트워크 에러가 아니면 throw하지 않으므로 직접 상태 확인 필요
                        if (response.status !== 200) {
                            throw new Error(`S3 업로드 실패 (Status: ${response.status})`);
                        }

                        // 4. 업로드 후 DB에 반영
                        reflectToDatabaseOnAdd(sanitizedName);
                    }
                }
                catch(error) {
                    addAlert({
                        title: '파일 업로드 오류',
                        message: ['파일 업로드 중 오류가 발생했습니다. 다시 시도해 주세요.', error.message || error.toString()]});
                }
                finally {
                    setUploading(false);
                }
            }
        }
    };

    const onClickFindFile = () => {
        if(fileInputRef.current) {
            fileInputRef.current.value='';
            fileInputRef.current.click();
        }
    };

    const loadinAny = actionDisabled || uploading || downloading
    || responseAdd.loading || responseRemove.loading
    || responseGetDownloadUrl.loading;

    return(
        <ListContainer>
            <FileLinkTitle>
                <span style={{backgroundColor:'white'}}>첨부 파일</span>
                <input
                    type="file"
                    accept="*/*"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={onChooseFile}
                />
            </FileLinkTitle>
            {files.length === 0 && (
                <Box style={{fontSize:'0.9rem', color:'#888', marginTop:10}}>첨부된 파일이 없습니다.</Box>
            )}
            {
                files.map((file) => {
                    return(
                        <FileLinkRow key={file.index}>
                            <FileLinkBox>
                                <NonCapButton
                                    onClick={() => onClickDownload(file.index, file.name)}
                                    size={AppWord.SMALL}
                                    variant={AppStyle.mui.text}
                                >
                                    {file.name}
                                </NonCapButton>
                            </FileLinkBox>
                            {
                                isPoster &&
                                <IconButton
                                    color={AppStyle.mui.warning}
                                    onClick={() => onClickRemove(file.index)}
                                    disabled={loadinAny}
                                >
                                    <DeleteForeverIcon fontSize={AppWord.SMALL}/>
                                </IconButton>
                            }
                        </FileLinkRow>
                    );
                })
            }
                {
                    isPoster && files.length < 5 &&
                    <Box>
                        <Button
                            variant={AppStyle.mui.contained}
                            color={AppStyle.mui.primary}
                            onClick={onClickFindFile}
                            startIcon={<AttachFileIcon />}
                            disabled={loadinAny}
                            size={AppWord.SMALL}
                        >
                            파일추가
                        </Button>
                    </Box>
                }
        </ListContainer>
    );
}